import { memo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { ProgressBar } from '@aimsos/ui';

import { TRAIT_EXPIRING_SOON_DAYS } from '../../constants/intelligence.js';
import { daysUntil, formatTraitDate, validityLeftPercent } from '../../utils/traitDates.js';

/**
 * How long a trait stays valid (CT-5715): the bar is the share of its validity
 * LEFT, then "Since …" on the left and "Expires … · in N days" on the right, one
 * line as in the prototype. Amber once it expires soon, red once it has.
 */
const TraitValidity = memo(function TraitValidity({ since, expiresAt }) {
  const { t, i18n } = useTranslation('unified-profiles');
  const language = i18n?.language;

  const left = validityLeftPercent(since, expiresAt);
  const days = daysUntil(expiresAt);
  const sinceLabel = formatTraitDate(since, language);
  const expiresLabel = formatTraitDate(expiresAt, language);

  let tone = 'green';
  if (days != null && days < 0) tone = 'red';
  else if (days != null && days <= TRAIT_EXPIRING_SOON_DAYS) tone = 'amber';

  return (
    <div data-testid="trait-validity" className="flex flex-col gap-2">
      {left != null ? (
        <ProgressBar
          value={left}
          tone={tone}
          showLabel={false}
          ariaLabel={t('intelligence.traits.detail.validityLeft')}
        />
      ) : null}
      <div data-testid="trait-validity-dates" className="flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5 text-sm text-content-secondary">
        {sinceLabel ? <span>{t('intelligence.traits.detail.since', { date: sinceLabel })}</span> : null}
        {expiresLabel && days != null ? (
          <span>
            {days < 0
              ? t('intelligence.traits.detail.expired', { date: expiresLabel })
              : t('intelligence.traits.detail.expiresIn', { date: expiresLabel, count: days })}
          </span>
        ) : null}
      </div>
    </div>
  );
});

export default TraitValidity;
